import { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useGetMarketingConsentVersionsQuery } from '@/api/tenants/marketingConsentVersions';

interface PickemMarketingConsentSectionProps {
  tenantId?: string;
  disabled?: boolean;
  marketingConsentVersionId?: string;
  onMarketingConsentVersionChange: (versionId: string) => void;
}

export function PickemMarketingConsentSection({
  tenantId,
  disabled = false,
  marketingConsentVersionId,
  onMarketingConsentVersionChange,
}: PickemMarketingConsentSectionProps) {
  const { data: versionsData, isLoading } = useGetMarketingConsentVersionsQuery(tenantId);
  const versions = versionsData?.versions || [];
  const defaultVersion = versions.find(v => v.isDefault);

  const [selectedVersionId, setSelectedVersionId] = useState<string>(
    marketingConsentVersionId || defaultVersion?.id || ''
  );

  useEffect(() => {
    const versionId = marketingConsentVersionId || defaultVersion?.id || '';
    if (versionId && versionId !== selectedVersionId) {
      setSelectedVersionId(versionId);
    }
    if (!marketingConsentVersionId && versionId) {
      onMarketingConsentVersionChange(versionId);
    }
  }, [versions, defaultVersion, marketingConsentVersionId]);

  const handleVersionChange = (versionId: string) => {
    setSelectedVersionId(versionId);
    onMarketingConsentVersionChange(versionId);
  };

  if (!tenantId) {
    return (
      <p className="text-sm text-muted-foreground pl-1">
        Select a tenant to choose a marketing consent version.
      </p>
    );
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground pl-1">Loading consent versions...</p>;
  }

  if (versions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground pl-1">
        No marketing consent versions for this tenant. Create one under Tenants → Marketing Consent.
      </p>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-2 pl-1 border-l-2 border-muted ml-2">
        <Label htmlFor="marketing-consent-version-select">Marketing Consent Version</Label>
        <Select value={selectedVersionId} onValueChange={handleVersionChange} disabled={disabled}>
          <SelectTrigger id="marketing-consent-version-select" className="max-w-md">
            <SelectValue placeholder="Select a consent version" />
          </SelectTrigger>
          <SelectContent>
            {versions.map(v => (
              <SelectItem key={v.id} value={v.id}>
                {v.title}
                {v.isDefault && ' (Default)'}
                {v.version ? ` - ${v.version}` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Tooltip>
          <TooltipTrigger asChild>
            <p className="text-xs text-muted-foreground cursor-default">
              Shown to players before making picks when marketing consent is enabled.
            </p>
          </TooltipTrigger>
          <TooltipContent className="max-w-sm">
            Manage versions under Tenants → Marketing Consent. The default version is pre-selected
            for new contests.
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
